import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useConsent } from "@/store/consent";

const KEY = "sl_store_saved_v1";
const SAVED_LIMIT = 100;
const WishlistContext = createContext(null);

const readSaved = () => {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(value)
      ? value.filter((item) => item?.product_id && item?.slug).slice(-SAVED_LIMIT)
      : [];
  } catch {
    return [];
  }
};

const savedProduct = (product) => ({
  product_id: product.product_id,
  slug: product.slug,
  title: product.title,
  part_number: product.part_number,
  price_from: product.price_from,
  price_to: product.price_to,
  photo: product.photo,
  available: product.available,
  conditions: product.conditions,
  brand: product.brand,
  appliance_type: product.appliance_type,
  category: product.category,
});

export function WishlistProvider({ children }) {
  const { canUseFunctional } = useConsent();
  const [saved, setSaved] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(false);
    if (!canUseFunctional) {
      setSaved([]);
      return;
    }
    setSaved(readSaved());
    setHydrated(true);
  }, [canUseFunctional]);
  useEffect(() => { if (canUseFunctional && hydrated) localStorage.setItem(KEY, JSON.stringify(saved)); }, [canUseFunctional, hydrated, saved]);

  const value = useMemo(
    () => ({
      saved,
      has: (productId) => saved.some((item) => item.product_id === productId),
      toggle: (product) => {
        if (!canUseFunctional) return;
        setSaved((items) => {
          const found = items.some((item) => item.product_id === product.product_id);
          return found
            ? items.filter((item) => item.product_id !== product.product_id)
            : [...items, savedProduct(product)].slice(-SAVED_LIMIT);
        });
      },
      remove: (productId) => {
        if (!canUseFunctional) return;
        setSaved((items) => items.filter((item) => item.product_id !== productId));
      },
    }),
    [canUseFunctional, saved],
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export const useWishlist = () => useContext(WishlistContext);